/**
 * Monitor side panel (DEC-187 decision 6, UI_SDD §R.3).
 *
 * While running: "Live findings, newest first", the last 20 from the stream, fade-in.
 * Finished: "Top findings, most severe first", the report's first five `top_findings`
 * and "See all N findings".
 */
import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import type { FindingEvent, Severity, TopFinding } from '../../types/api'
import { SeverityBadge, TonePill } from '../shared/Badge'
import { CATEGORY_LABELS } from '../../types/api'

const SEVERITIES: Severity[] = ['critical', 'high', 'medium', 'low']

function timeAgo(iso: string): string {
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000))
  if (s < 60) return `${s}s`
  return `${Math.floor(s / 60)}m`
}

export interface TopFindingsPanel {
  scanId: string
  /** The report's `top_findings`; undefined while it loads. */
  findings: TopFinding[] | undefined
  /** Failed to load the report. */
  failed?: boolean
  /** Every finding of the scan, for "See all N findings". */
  total: number
}

/** The one pill style (DEC-193), in a fixed slot so the text after it lines up. */
function SevChip({ severity }: { severity: string }) {
  const known = SEVERITIES.find((k) => k === severity)
  return (
    <span className="w-[86px] flex-none">
      {known ? <SeverityBadge severity={known} /> : <TonePill tone="neutral">{severity}</TonePill>}
    </span>
  )
}

export function LiveRail({
  findings,
  frozen,
  emptyText = 'No findings yet.',
  top,
}: {
  findings: FindingEvent[]
  frozen: boolean
  /** DEC-185: a scan read back from saved data has no live feed to show. */
  emptyText?: string
  /** DEC-187: a finished scan shows its most severe findings instead of the feed. */
  top?: TopFindingsPanel
}) {
  if (top) {
    const list = (top.findings ?? []).slice(0, 5)
    return (
      <aside
        data-testid="top-findings"
        className="w-[300px] flex-none bg-panel border border-hairline rounded-md overflow-hidden self-start"
      >
        <div className="px-4 py-3 border-b border-fill-subtle flex items-center gap-2">
          <span className="font-semibold text-[10px] leading-none tracking-[2px] uppercase text-muted">Top findings</span>
          <span className="ml-auto text-[9px] text-faint">most severe first</span>
        </div>
        {top.findings === undefined ? (
          <div className="px-4 py-6 text-[11px] text-faint">
            {top.failed ? 'Top findings could not be loaded.' : 'Loading top findings…'}
          </div>
        ) : list.length === 0 ? (
          <div className="px-4 py-6 text-[11px] text-faint">No findings in this scan.</div>
        ) : (
          list.map((f) => (
            <div key={f.finding_id} className="px-4 py-[10px] border-b border-fill-subtle">
              <div className="flex items-center gap-[7px] mb-1">
                <SevChip severity={f.severity} />
                <span className="text-[11px] tabular-nums text-muted truncate min-w-0">
                  {f.file}
                  {f.line_start ? `:${f.line_start}` : ''}
                </span>
              </div>
              <div className="text-[11px] leading-[1.35] text-ink line-clamp-2 break-words">{f.human_summary}</div>
            </div>
          ))
        )}
        {top.total > 0 && (
          <Link
            to={`/scans/${top.scanId}/findings`}
            className="flex items-center gap-[6px] px-4 py-3 font-semibold text-[12px] text-accent-text hover:underline"
          >
            See all {top.total} finding{top.total === 1 ? '' : 's'} <ArrowRight className="w-[13px] h-[13px]" />
          </Link>
        )}
      </aside>
    )
  }

  return (
    <aside className="w-[300px] flex-none bg-panel border border-hairline rounded-md overflow-hidden self-start">
      <div className="px-4 py-3 border-b border-fill-subtle flex items-center gap-2">
        <span
          className={`w-[6px] h-[6px] rounded-full ${frozen ? 'bg-faint' : 'bg-accent animate-pulse'}`}
        />
        <span className="font-semibold text-[10px] leading-none tracking-[2px] uppercase text-muted">
          Live findings
        </span>
        <span className="ml-auto text-[9px] text-faint">{frozen ? 'final' : 'newest first'}</span>
      </div>
      <div className="max-h-[560px] overflow-y-auto">
        {findings.length === 0 ? (
          <div className="px-4 py-6 text-[11px] text-faint">{emptyText}</div>
        ) : (
          findings.map((f) => (
            <div key={f.finding_id} className="px-4 py-[10px] border-b border-fill-subtle last:border-0 animate-fade-in">
              <div className="flex items-center gap-[7px] mb-1">
                <SevChip severity={f.severity} />
                <span className="text-[11px] text-muted truncate min-w-0">{CATEGORY_LABELS[f.category] ?? f.category}</span>
                <span className="ml-auto text-[11px] tabular-nums text-faint flex-none">{timeAgo(f.timestamp)}</span>
              </div>
              <div className="text-[11px] text-muted truncate mb-[2px]">{f.file}</div>
              <div className="text-[11px] leading-[1.35] text-ink line-clamp-2 break-words">{f.human_summary}</div>
            </div>
          ))
        )}
      </div>
    </aside>
  )
}
